import { CaretRight } from "@phosphor-icons/react";
import { type AnchorHTMLAttributes, type ReactNode } from "react";
import styled from "@emotion/styled";
import { twRing } from "@/lib/emotionTw";

export type BreadcrumbItem = {
  label: ReactNode;
  href?: string;
  onClick?: AnchorHTMLAttributes<HTMLAnchorElement>["onClick"];
};

export type BreadcrumbsProps = {
  items: BreadcrumbItem[];
  className?: string;
};

/* Exact compiled output of the pre-migration Tailwind utilities. */

const BreadcrumbsList = styled("ol", {
  label: "AgenticBreadcrumbs-list"
})({
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  gap: ".25rem",
  fontSize: ".875rem",
  lineHeight: "1.25rem",
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-muted) / var(--tw-text-opacity, 1))"
});

const BreadcrumbsItem = styled("li", {
  label: "AgenticBreadcrumbs-item"
})({
  display: "flex",
  alignItems: "center",
  gap: ".25rem"
});

const BreadcrumbsLink = styled("a", {
  label: "AgenticBreadcrumbs-link"
})({
  borderRadius: "var(--bfrs-radius-sm)",
  "&:hover": {
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-text-primary) / var(--tw-text-opacity, 1))"
  },
  "&:focus-visible": {
    outline: "2px solid transparent",
    outlineOffset: "2px",
    ...twRing("2px", "hsl(var(--bfrs-color-border-focus) / var(--tw-ring-opacity, 1))"),
    "--tw-ring-opacity": "1"
  }
});

const BreadcrumbsCurrent = styled("span", {
  label: "AgenticBreadcrumbs-current",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { last: boolean } }>(({ ownerState }) => [
  ownerState.last && {
    fontWeight: 500,
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-text-primary) / var(--tw-text-opacity, 1))"
  }
]);

export function Breadcrumbs({ items, className }: BreadcrumbsProps) {
  return (
    <nav aria-label="Breadcrumb" className={className}>
      <BreadcrumbsList>
        {items.map((item, index) => {
          const isLast = index === items.length - 1;
          return (
            <BreadcrumbsItem key={index}>
              {item.href && !isLast ? (
                <BreadcrumbsLink href={item.href} onClick={item.onClick}>
                  {item.label}
                </BreadcrumbsLink>
              ) : (
                <BreadcrumbsCurrent ownerState={{ last: isLast }}>{item.label}</BreadcrumbsCurrent>
              )}
              {!isLast && <CaretRight size={12} aria-hidden="true" />}
            </BreadcrumbsItem>
          );
        })}
      </BreadcrumbsList>
    </nav>
  );
}
